import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DefaultValue, atom, selector, useRecoilValue, useSetRecoilState, } from 'recoil';
import { Settings } from '../Settings';
import { getEmptyMediaDevices, getMediaDevices } from '../utils/media';
import { getUserMedia } from '../utils/mediaDevices';
import { safeRetrieve, safeStore } from '../utils/storage';
const mediaState = atom({
    key: 'mediaState',
    default: {
        mediaDevices: getEmptyMediaDevices(),
        defaultAudioDeviceId: undefined,
        selectedAudioDeviceId: safeRetrieve('audioDeviceId'),
        defaultVideoDeviceId: undefined,
        selectedVideoDeviceId: safeRetrieve('videoDeviceId'),
        displaySurface: undefined,
        muteAudio: false,
        muteVideo: false,
        permissions: {
            audio: false,
            video: false,
        },
    },
});
export function useLoadInitialPermissionsEffect() {
    const setMediaState = useSetRecoilState(mediaState);
    useEffect(() => {
        let mounted = true;
        async function loadInitialPermissions() {
            let audio = false;
            let video = false;
            try {
                for (const device of await navigator.mediaDevices.enumerateDevices()) {
                    if (!device.label) {
                        continue;
                    }
                    if (!audio && device.kind === 'audioinput') {
                        audio = true;
                    }
                    if (!video && device.kind === 'videoinput') {
                        video = true;
                    }
                }
            }
            catch (_) { }
            if (mounted) {
                setMediaState((current) => ({
                    ...current,
                    permissions: {
                        audio,
                        video,
                    },
                }));
            }
        }
        loadInitialPermissions();
        return () => {
            mounted = false;
        };
    }, [setMediaState]);
}
export function useVideoDevices() {
    const { mediaDevices, permissions: { video }, } = useRecoilValue(mediaState);
    return video ? mediaDevices.videoDevices : [];
}
export function useAudioDevices() {
    const { mediaDevices, permissions: { audio }, } = useRecoilValue(mediaState);
    return audio ? mediaDevices.audioDevices : [];
}
const defaultVideoDeviceState = selector({
    key: 'defaultVideoDeviceState',
    get: ({ get }) => {
        const { defaultVideoDeviceId, mediaDevices, permissions: { video }, } = get(mediaState);
        return defaultVideoDeviceId && video
            ? mediaDevices.videoDevicesById[defaultVideoDeviceId]
            : undefined;
    },
});
export function useDefaultVideoDevice() {
    return useRecoilValue(defaultVideoDeviceState);
}
const currentVideoDeviceState = selector({
    key: 'currentVideoDeviceState',
    get: ({ get }) => {
        const { defaultVideoDeviceId, selectedVideoDeviceId, mediaDevices: { videoDevicesById }, permissions: { video }, } = get(mediaState);
        if (!video) {
            return undefined;
        }
        const defaultVideoDevice = defaultVideoDeviceId
            ? videoDevicesById[defaultVideoDeviceId]
            : undefined;
        const selectedVideoDevice = selectedVideoDeviceId
            ? videoDevicesById[selectedVideoDeviceId]
            : undefined;
        return selectedVideoDevice || defaultVideoDevice;
    },
});
export function useCurrentVideoDevice() {
    return useRecoilValue(currentVideoDeviceState);
}
const currentAudioDeviceState = selector({
    key: 'currentAudioDeviceState',
    get: ({ get }) => {
        const { defaultAudioDeviceId, selectedAudioDeviceId, mediaDevices: { audioDevicesById }, permissions: { audio }, } = get(mediaState);
        if (!audio) {
            return undefined;
        }
        const defaultAudioDevice = defaultAudioDeviceId
            ? audioDevicesById[defaultAudioDeviceId]
            : undefined;
        const selectedAudioDevice = selectedAudioDeviceId
            ? audioDevicesById[selectedAudioDeviceId]
            : undefined;
        return selectedAudioDevice || defaultAudioDevice;
    },
});
export function useCurrentAudioDevice() {
    return useRecoilValue(currentAudioDeviceState);
}
export function useCurrentDevice(kind) {
    const audioDevice = useCurrentAudioDevice();
    const videoDevice = useCurrentVideoDevice();
    return kind === 'audio' ? audioDevice : videoDevice;
}
const defaultAudioDeviceState = selector({
    key: 'defaultAudioDevice',
    get: ({ get }) => {
        const { defaultAudioDeviceId, mediaDevices: { audioDevicesById }, permissions: { audio }, } = get(mediaState);
        return audio && defaultAudioDeviceId
            ? audioDevicesById[defaultAudioDeviceId]
            : undefined;
    },
});
export function useDefaultAudioDevice() {
    return useRecoilValue(defaultAudioDeviceState);
}
const selectedVideoDeviceState = selector({
    key: 'selectedVideoDeviceState',
    get: ({ get }) => {
        const { selectedVideoDeviceId, mediaDevices: { videoDevicesById }, permissions: { video }, } = get(mediaState);
        return video && selectedVideoDeviceId
            ? videoDevicesById[selectedVideoDeviceId]
            : undefined;
    },
    set: ({ get, set }, newValue) => {
        const selectedVideoDeviceId = newValue instanceof DefaultValue ? undefined : newValue?.deviceId;
        set(mediaState, {
            ...get(mediaState),
            selectedVideoDeviceId,
        });
        safeStore('videoDeviceId', selectedVideoDeviceId);
    },
});
export function useSetSelectedVideoDevice() {
    return useSetRecoilState(selectedVideoDeviceState);
}
const selectedAudioDeviceState = selector({
    key: 'selectedAudioDeviceState',
    get: ({ get }) => {
        const { selectedAudioDeviceId, mediaDevices: { audioDevicesById }, permissions: { audio }, } = get(mediaState);
        return audio && selectedAudioDeviceId
            ? audioDevicesById[selectedAudioDeviceId]
            : undefined;
    },
    set: ({ get, set }, newValue) => {
        const selectedAudioDeviceId = newValue instanceof DefaultValue ? undefined : newValue?.deviceId;
        set(mediaState, {
            ...get(mediaState),
            selectedAudioDeviceId,
        });
        safeStore('audioDeviceId', selectedAudioDeviceId);
    },
});
export function useSetSelectedAudioDevice() {
    return useSetRecoilState(selectedAudioDeviceState);
}
export function useWatchDevicesEffect() {
    const setMediaState = useSetRecoilState(mediaState);
    useEffect(function assignCurrentDevices() {
        let mounted = true;
        if (!('mediaDevices' in navigator)) {
            return () => { };
        }
        async function onDeviceListChange() {
            const mediaDevices = await getMediaDevices();
            if (!mounted) {
                return;
            }
            setMediaState((state) => {
                return { ...state, mediaDevices };
            });
        }
        navigator.mediaDevices.addEventListener('devicechange', onDeviceListChange);
        onDeviceListChange();
        return () => {
            mounted = false;
            navigator.mediaDevices.removeEventListener('devicechange', onDeviceListChange);
        };
    }, [setMediaState]);
}
const displaySurfaceState = selector({
    key: 'displaySurfaceState',
    get: ({ get }) => {
        return get(mediaState).displaySurface;
    },
    set: ({ get, set }, newValue) => {
        const currentState = get(mediaState);
        set(mediaState, {
            ...currentState,
            displaySurface: newValue instanceof DefaultValue ? undefined : newValue,
        });
    },
});
export function useDisplaySurface() {
    return useRecoilValue(displaySurfaceState);
}
export const useSetDisplaySurface = () => {
    return useSetRecoilState(displaySurfaceState);
};
const muteAudioState = selector({
    key: 'muteAudioState',
    get: ({ get }) => {
        return get(mediaState).muteAudio;
    },
    set: ({ get, set }, newValue) => {
        set(mediaState, {
            ...get(mediaState),
            muteAudio: newValue instanceof DefaultValue ? false : newValue,
        });
    },
});
const muteVideoState = selector({
    key: 'muteVideoState',
    get: ({ get }) => {
        return get(mediaState).muteVideo;
    },
    set: ({ get, set }, newValue) => {
        set(mediaState, {
            ...get(mediaState),
            muteVideo: newValue instanceof DefaultValue ? false : newValue,
        });
    },
});
export function useIsAudioOnMute() {
    return useRecoilValue(muteAudioState);
}
export const useSetMuteAudio = () => {
    return useSetRecoilState(muteAudioState);
};
export function useIsVideoOnMute() {
    return useRecoilValue(muteVideoState);
}
export const useSetMuteVideo = () => {
    return useSetRecoilState(muteVideoState);
};
function useDisplayMediaStream() {
    const displaySurface = useDisplaySurface();
    const setDisplaySurface = useSetDisplaySurface();
    const [displayStream, setDisplayStream] = useState();
    useEffect(() => {
        let mounted = true;
        if (!displaySurface) {
            setDisplayStream(undefined);
            return () => { };
        }
        (async () => {
            try {
                const displayMediaStream = await navigator.mediaDevices.getDisplayMedia({
                    video: {
                        displaySurface,
                        frameRate: {
                            ideal: Settings.videoEncoderDefaultFrameRate,
                        },
                    },
                    audio: {
                        sampleRate: Settings.audioSampleRate,
                        suppressLocalAudioPlayback: true,
                    },
                });
                if (displayMediaStream.getVideoTracks().length > 0) {
                    const videoTrack = displayMediaStream.getVideoTracks()[0];
                    videoTrack.applyConstraints({
                        frameRate: {
                            min: Settings.videoEncoderDefaultFrameRate,
                        },
                    });
                }
                if (mounted) {
                    setDisplayStream(displayMediaStream);
                }
            }
            catch (_) {
                setDisplaySurface(undefined);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [displaySurface, setDisplaySurface]);
    // Apply useTrackEnderEffect to all tracks in the displayStream
    useEffect(() => {
        if (!displayStream)
            return;
        const tracks = displayStream.getTracks();
        const trackEndHandler = () => {
            setDisplayStream(undefined);
            setDisplaySurface(undefined);
        };
        // Add ended event listener to all tracks
        for (const track of tracks) {
            track.addEventListener('ended', trackEndHandler);
        }
        return () => {
            // Clean up listeners when component unmounts or stream changes
            for (const track of tracks) {
                track.removeEventListener('ended', trackEndHandler);
                track.stop();
            }
        };
    }, [displayStream, setDisplaySurface]);
    return displayStream;
}
function useTrackEnderEffect(track, onEnded) {
    const onEndedRef = useRef(onEnded);
    if (onEndedRef.current !== onEnded) {
        onEndedRef.current = onEnded;
    }
    useEffect(() => {
        let mounted = true;
        if (!track) {
            return () => { };
        }
        function onTrackEnd(e) {
            if (!mounted) {
                return;
            }
            if (e.currentTarget instanceof MediaStreamTrack) {
                onEndedRef.current?.(e.currentTarget);
            }
        }
        track.addEventListener('ended', onTrackEnd);
        return () => {
            mounted = false;
            track.removeEventListener('ended', onTrackEnd);
            track.stop();
        };
    }, [track]);
}
function useAudioTrack() {
    const audioDevice = useCurrentAudioDevice();
    const canAccessMicrophone = useCanAccessMicrophone();
    const [track, setTrack] = useState();
    useEffect(() => {
        let mounted = true;
        (async () => {
            // Only attempt to get audio track if permission is granted
            if (!canAccessMicrophone) {
                return;
            }
            try {
                let constraints = true;
                if (audioDevice) {
                    constraints = {
                        deviceId: { exact: audioDevice.deviceId },
                    };
                }
                const stream = await getUserMedia({
                    audio: constraints,
                    video: false,
                });
                if (!mounted) {
                    return;
                }
                setTrack(stream.getAudioTracks()[0]);
            }
            catch (_) {
                if (!mounted) {
                    return;
                }
                setTrack(undefined);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [audioDevice, canAccessMicrophone]);
    useTrackEnderEffect(track, (track) => {
        setTrack((prev) => (prev === track ? undefined : prev));
    });
    return track;
}
function useVideoTrack(enabled = true) {
    const videoDevice = useCurrentVideoDevice();
    const canAccessCamera = useCanAccessCamera();
    const [track, setTrack] = useState();
    useEffect(() => {
        let mounted = true;
        if (!enabled) {
            setTrack(undefined);
            return () => { };
        }
        (async () => {
            // Only attempt to get video track if permission is granted
            if (!canAccessCamera) {
                return;
            }
            try {
                let constraints = true;
                if (videoDevice) {
                    constraints = {
                        deviceId: { exact: videoDevice.deviceId },
                    };
                }
                const stream = await getUserMedia({
                    audio: false,
                    video: constraints,
                });
                if (!mounted) {
                    return;
                }
                setTrack(stream.getVideoTracks()[0]);
            }
            catch (_) {
                if (!mounted) {
                    return;
                }
                setTrack(undefined);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [videoDevice, canAccessCamera, enabled]);
    useTrackEnderEffect(track, (track) => {
        setTrack((prev) => (prev === track ? undefined : prev));
    });
    return track;
}
export function useMediaStream() {
    const displayStream = useDisplayMediaStream();
    const audioTrack = useAudioTrack();
    const videoTrack = useVideoTrack(!displayStream);
    const isAudioOnMute = useIsAudioOnMute();
    const isVideoOnMute = useIsVideoOnMute();
    const mediaStream = useMemo(() => {
        const tracks = [];
        if (displayStream?.getVideoTracks().length) {
            tracks.push(...displayStream.getVideoTracks());
        }
        if (videoTrack) {
            tracks.push(videoTrack);
        }
        if (displayStream?.getAudioTracks().length) {
            tracks.push(...displayStream.getAudioTracks());
        }
        if (audioTrack) {
            tracks.push(audioTrack);
        }
        return new MediaStream(tracks);
    }, [displayStream, videoTrack, audioTrack]);
    useEffect(() => {
        for (const track of mediaStream.getTracks()) {
            if (track.kind === 'audio') {
                track.enabled = !isAudioOnMute;
            }
            if (track.kind === 'video' && !track.getSettings().displaySurface) {
                track.enabled = !isVideoOnMute && !displayStream;
            }
        }
    }, [mediaStream, isAudioOnMute, isVideoOnMute, displayStream]);
    useEffect(() => () => {
        for (const track of mediaStream.getTracks()) {
            mediaStream.removeTrack(track);
        }
    }, [mediaStream]);
    return mediaStream;
}
export function useIsSharingScreen(mediaStream) {
    return useMemo(() => {
        for (const track of mediaStream.getVideoTracks()) {
            if (isScreenSharingTrack(track)) {
                return true;
            }
        }
        return false;
    }, [mediaStream]);
}
/**
 * Checks if given track carries a shared screen.
 */
function isScreenSharingTrack(track) {
    return !!track.getSettings().displaySurface;
}
export function useIsUsingFrontCamera(mediaStream) {
    return useMemo(() => {
        for (const track of mediaStream.getVideoTracks()) {
            if (isUserFacingCameraTrack(track)) {
                return true;
            }
        }
        return false;
    }, [mediaStream]);
}
/**
 * Checks if track's facing mode indicates user-facing camera. If facing
 * mode isn't defined it infers the mode from other track properties.
 */
export function isUserFacingCameraTrack(track) {
    return (!isScreenSharingTrack(track) &&
        (track.getSettings().facingMode === 'user' ||
            /facetime hd camera|integrated/i.test(track.label)));
}
export function useVolumeEffect(mediaStream, onUpdate) {
    const onUpdateRef = useRef(onUpdate);
    if (onUpdateRef.current !== onUpdate) {
        onUpdateRef.current = onUpdate;
    }
    useEffect(() => {
        if (!mediaStream) {
            return;
        }
        let audioContext;
        let analyser;
        let container;
        let animationFrameId;
        let source;
        let lastKnownVolume;
        try {
            audioContext = new AudioContext();
            analyser = audioContext.createAnalyser();
            source = audioContext.createMediaStreamSource(mediaStream);
            container = new Uint8Array(analyser.frequencyBinCount);
            analyser.fftSize = 512;
            source.connect(analyser);
            function updateVolume() {
                if (!analyser || !container) {
                    return;
                }
                analyser.getByteFrequencyData(container);
                const rawVolume = container.reduce((a, b) => a + b, 0) / container.length;
                const volume = +Math.min(1, rawVolume / 32).toFixed(2);
                if (lastKnownVolume !== volume) {
                    onUpdateRef.current?.(volume);
                    lastKnownVolume = volume;
                }
                animationFrameId = requestAnimationFrame(updateVolume);
            }
            updateVolume();
        }
        catch (_) { }
        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
            source?.disconnect();
            analyser?.disconnect();
            audioContext?.close();
        };
    }, [mediaStream]);
}
export function useCanAccessCamera() {
    const { permissions: { video }, mediaDevices, } = useRecoilValue(mediaState);
    return video && mediaDevices.videoDevices.length > 0;
}
export function useCanAccessMicrophone() {
    const { permissions: { audio }, mediaDevices, } = useRecoilValue(mediaState);
    return audio && mediaDevices.audioDevices.length > 0;
}
export function useHasMedia(kind) {
    const { mediaDevices } = useRecoilValue(mediaState);
    return kind === 'audio' ? mediaDevices.hasAudio : mediaDevices.hasVideo;
}
export function useRequestMediaPermission() {
    const setMediaState = useSetRecoilState(mediaState);
    return useCallback(async (kind) => {
        let stream;
        try {
            stream = await getUserMedia({
                audio: kind === 'audio' || kind === 'both',
                video: kind === 'video' || kind === 'both',
            });
            const mediaDevices = await getMediaDevices();
            const newDefaultAudioDeviceId = stream
                .getAudioTracks()[0]
                ?.getSettings().deviceId;
            const newDefaultVideoDeviceId = stream
                .getVideoTracks()[0]
                ?.getSettings().deviceId;
            setMediaState((state) => {
                let defaultAudioDeviceId = state.defaultAudioDeviceId;
                if (!defaultAudioDeviceId ||
                    !mediaDevices.audioDevicesById[defaultAudioDeviceId]) {
                    defaultAudioDeviceId = newDefaultAudioDeviceId;
                }
                let defaultVideoDeviceId = state.defaultVideoDeviceId;
                if (!defaultVideoDeviceId ||
                    !mediaDevices.videoDevicesById[defaultVideoDeviceId]) {
                    defaultVideoDeviceId = newDefaultVideoDeviceId;
                }
                let selectedAudioDeviceId = state.selectedAudioDeviceId;
                if (selectedAudioDeviceId &&
                    !mediaDevices.audioDevicesById[selectedAudioDeviceId]) {
                    selectedAudioDeviceId = undefined;
                }
                let selectedVideoDeviceId = state.selectedVideoDeviceId;
                if (selectedVideoDeviceId &&
                    !mediaDevices.videoDevicesById[selectedVideoDeviceId]) {
                    selectedVideoDeviceId = undefined;
                }
                const newPermissions = kind === 'both'
                    ? {
                        audio: true,
                        video: true,
                    }
                    : kind === 'audio'
                        ? { audio: true }
                        : { video: true };
                return {
                    ...state,
                    defaultAudioDeviceId,
                    defaultVideoDeviceId,
                    selectedAudioDeviceId,
                    selectedVideoDeviceId,
                    mediaDevices,
                    permissions: {
                        ...state.permissions,
                        ...newPermissions,
                    },
                };
            });
        }
        catch (e) {
            if (e instanceof Error) {
                switch (e.name) {
                    case 'NotAllowedError':
                        return;
                    default:
                }
            }
            console.warn('Failed to get media', kind, e);
        }
        finally {
            const tracks = stream?.getTracks() || [];
            for (const track of tracks) {
                track.stop();
            }
            stream = undefined;
        }
    }, [setMediaState]);
}
